import React, { useState, useEffect } from 'react';
import { Zap, Send } from 'lucide-react';
import { api } from '../api';
import { useTranslation } from 'react-i18next';

const WorkshopPlanner = ({ user }) => {
  const { t } = useTranslation();

  const WORKSHOP_PLANNER_STARTERS = [
    t('workshopPlanner.starter1'),
    t('workshopPlanner.starter2'),
    t('workshopPlanner.starter3'),
    t('workshopPlanner.starter4'),
    t('workshopPlanner.starter5'),
  ];

  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Hi ${user.full_name || 'there'}! I'm your AI Workshop Planning Copilot.\n\nI can help you with:\n- **Scheduling optimization** — best timing and frequency for your workshops\n- **Audience targeting** — reaching the right learners for each course\n- **Content recommendations** — which courses to deliver next\n- **Impact maximization** — tips to increase attendance and feedback scores\n\nWhat would you like to plan today?`
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const chatEndRef = React.useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    const msgText = (text || input).trim();
    if (!msgText || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msgText }]);
    setLoading(true);
    try {
      const data = await api.request('/workshop-planner/chat', {
        method: 'POST',
        body: JSON.stringify({ message: msgText, session_id: sessionId })
      });
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: t('workshopPlanner.errorMessage') }]);
    } finally {
      setLoading(false);
    }
  };

  const renderMessage = (text) => text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br/>');

  return (
    <div className="flex flex-col h-[calc(100vh-120px)] max-w-4xl mx-auto">
      <div className="bg-levelup-dark rounded-xl p-5 text-white mb-4 flex items-center space-x-4">
        <div className="bg-levelup-accent/20 p-3 rounded-xl">
          <Zap className="text-levelup-accent" size={24} />
        </div>
        <div>
          <h1 className="text-xl font-bold">{t('workshopPlanner.title')}</h1>
          <p className="text-gray-300 text-sm">{t('workshopPlanner.description')}</p>
        </div>
      </div>

      <div className="flex-1 bg-white rounded-xl shadow-md p-5 overflow-y-auto space-y-4 mb-4">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'assistant' && (
              <div className="w-8 h-8 bg-levelup-accent rounded-full flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                <Zap size={14} className="text-levelup-darker" />
              </div>
            )}
            <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
              msg.role === 'user' ? 'bg-levelup-dark text-white rounded-br-none' : 'bg-gray-50 text-gray-800 border border-gray-100 rounded-bl-none'
            }`}>
              <div dangerouslySetInnerHTML={{ __html: renderMessage(msg.content) }} />
            </div>
            {msg.role === 'user' && (
              <div className="w-8 h-8 bg-levelup-darker rounded-full flex items-center justify-center flex-shrink-0 ml-3 mt-1">
                <span className="text-white text-xs font-bold">{(user.full_name || 'U')[0].toUpperCase()}</span>
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="w-8 h-8 bg-levelup-accent rounded-full flex items-center justify-center flex-shrink-0 mr-3">
              <Zap size={14} className="text-levelup-darker" />
            </div>
            <div className="bg-gray-50 border border-gray-100 px-3 py-2 rounded-2xl rounded-bl-none flex items-center space-x-1.5">
              <div className="w-2 h-2 bg-levelup-accent rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
              <div className="w-2 h-2 bg-levelup-accent rounded-full animate-bounce" style={{animationDelay: '150ms'}} />
              <div className="w-2 h-2 bg-levelup-accent rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {messages.length === 1 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {WORKSHOP_PLANNER_STARTERS.map((s, i) => (
            <button key={i} onClick={() => sendMessage(s)}
              className="text-xs px-3 py-2 bg-levelup-accent/10 text-levelup-dark border border-levelup-accent/30 rounded-full hover:bg-levelup-accent/20 transition-all"
              data-testid={`starter-${i}`}>{s}
            </button>
          ))}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md p-3 flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
          placeholder={t('workshopPlanner.inputPlaceholder')}
          className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-levelup-accent focus:border-transparent text-sm"
          disabled={loading}
          data-testid="planner-input"
        />
        <button
          onClick={() => sendMessage()}
          disabled={!input.trim() || loading}
          className="px-5 py-2.5 bg-levelup-accent text-levelup-darker rounded-lg font-semibold text-sm hover:bg-levelup-accent-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          data-testid="planner-send-btn"
        >
          <Send size={16} />
          <span>{t('workshopPlanner.send')}</span>
        </button>
      </div>
    </div>
  );
};

export default WorkshopPlanner;
